import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, } from 'react';
function toastReducer(state, action) {
    switch (action.type) {
        case 'add':
            // Cap the stack so a loop of failures cannot fill the screen.
            return [...state, action.toast].slice(-4);
        case 'dismiss':
            return state.filter((toast) => toast.id !== action.id);
        default:
            return state;
    }
}
const ToastContext = createContext(null);
let toastCounter = 0;
export function ToastProvider({ children }) {
    const [toasts, dispatch] = useReducer(toastReducer, []);
    const push = useCallback((tone, message, durationMs) => {
        toastCounter += 1;
        dispatch({
            type: 'add',
            toast: { id: `toast-${toastCounter}`, tone, message, durationMs },
        });
    }, []);
    const api = useMemo(() => ({
        // Errors linger longer, because they usually need reading.
        success: (message, durationMs = 4_000) => push('success', message, durationMs),
        error: (message, durationMs = 7_000) => push('error', message, durationMs),
        info: (message, durationMs = 5_000) => push('info', message, durationMs),
        dismiss: (id) => dispatch({ type: 'dismiss', id }),
    }), [push]);
    return (_jsxs(ToastContext.Provider, { value: api, children: [children, _jsx(ToastViewport, { toasts: toasts, onDismiss: (id) => dispatch({ type: 'dismiss', id }) })] }));
}
export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used inside a <ToastProvider>');
    }
    return context;
}
const TONE_STYLES = {
    success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    error: 'border-rose-200 bg-rose-50 text-rose-900',
    info: 'border-slate-200 bg-white text-slate-900',
};
function ToastViewport({ toasts, onDismiss, }) {
    return (_jsx("div", { className: "pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:bottom-auto sm:right-0 sm:top-0 sm:items-end", "aria-live": "polite", "aria-atomic": "false", children: toasts.map((toast) => (_jsx(ToastItem, { toast: toast, onDismiss: onDismiss }, toast.id))) }));
}
function ToastItem({ toast, onDismiss, }) {
    useEffect(() => {
        const timer = setTimeout(() => onDismiss(toast.id), toast.durationMs);
        return () => clearTimeout(timer);
    }, [toast.id, toast.durationMs, onDismiss]);
    return (_jsxs("div", { className: clsx('pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border px-4 py-3 shadow-lg', 'animate-[fadeIn_150ms_ease-out]', TONE_STYLES[toast.tone]), role: toast.tone === 'error' ? 'alert' : 'status', children: [_jsx("p", { className: "flex-1 text-sm", children: toast.message }), _jsx("button", { type: "button", onClick: () => onDismiss(toast.id), className: "shrink-0 rounded p-0.5 text-current opacity-60 transition-opacity hover:opacity-100", "aria-label": "Dismiss notification", children: _jsx("svg", { className: "h-4 w-4", viewBox: "0 0 20 20", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" }) }) })] }));
}
//# sourceMappingURL=toast.js.map